export type LongwallsControlTowerMetrics = {
  generatedAt: string;
  throughput: {
    runsLast7d: number;
    runsPerDay: number;
    completedRuns: number;
  };
  quality: {
    successRate: number | null;
    passRuns: number;
    failRuns: number;
    evidenceCompletenessRate: number | null;
    medianIterations: number | null;
  };
  risk: {
    pendingApprovals: number;
    highRiskOpen: number;
    integrityAlerts: number;
  };
  cost: {
    sampledRuns: number;
    totalUsd: number;
    avgUsd: number | null;
  };
  leadTime: {
    sampledRuns: number;
    averageMs: number | null;
    medianMs: number | null;
    p95Ms: number | null;
  };
};

export type LongwallsMarketplacePack = {
  id: string;
  name: string;
  version: string;
  domain: string;
  archetype: string;
  maturity: "experimental" | "beta" | "stable";
  tags: string[];
  summary: string;
  defaultPromptVersion: string;
  defaultPolicyVersion: string;
  adoptionRuns: number;
  successfulRuns: number;
  adoptionSuccessRate: number | null;
  lastUsedAt: string | null;
};

export type LongwallsLearningRecommendation = {
  id: string;
  target: "prompt" | "policy" | "template";
  signature: string;
  occurrences: number;
  confidence: number;
  recommendation: string;
};

export type LongwallsLearningLoop = {
  generatedAt: string;
  confidence: number;
  topFailureSignatures: Array<{ signature: string; count: number }>;
  promptAdjustments: LongwallsLearningRecommendation[];
  policyAdjustments: LongwallsLearningRecommendation[];
  templateAdjustments: LongwallsLearningRecommendation[];
};

export type LongwallsReleaseRecommendation = {
  generatedAt: string;
  decision: "GO" | "PARTIAL_GO" | "NO_GO";
  confidence: number;
  score: number;
  reasons: string[];
  nextActions: string[];
  kpiSnapshot: {
    completedRuns: number;
    successRate: number | null;
    evidenceCompletenessRate: number | null;
    pendingApprovals: number;
    highRiskOpen: number;
    integrityAlerts: number;
    medianLeadTimeMs: number | null;
  };
};

export type LongwallsReleaseAttestationControl = {
  status: "pass" | "watch" | "fail";
  reason: string;
};

export type LongwallsReleaseAttestation = {
  kind: "release-attestation";
  schema: "longwalls.release-attestation@1";
  id: string;
  version: string;
  projectId: string;
  generatedAt: string;
  attestedAt: string;
  decision: "GO" | "PARTIAL_GO" | "NO_GO";
  score: number;
  confidence: number;
  statement: string;
  summary: {
    completedRuns: number;
    successRate: number | null;
    evidenceCompletenessRate: number | null;
    pendingApprovals: number;
    highRiskOpen: number;
    integrityAlerts: number;
    medianLeadTimeMs: number | null;
  };
  controls: {
    auditChain: LongwallsReleaseAttestationControl & { totalSignals: number };
    evidenceCoverage: LongwallsReleaseAttestationControl & { completenessRate: number | null };
    approvals: LongwallsReleaseAttestationControl & { pendingApprovals: number; highRiskOpen: number };
    runtimeStability: LongwallsReleaseAttestationControl & { completedRuns: number; successRate: number | null };
  };
  reasons: string[];
  nextActions: string[];
  evidence: {
    generatedFrom: "longwalls.productization@1";
    sourceRunIds: string[];
    artifactCount: number;
    runs: Array<{
      runId: string;
      workItemId: string;
      workItemTitle: string | null;
      status: "pending" | "running" | "succeeded" | "failed";
      stopReason: string | null;
      approvalStatus: "not_required" | "pending" | "approved" | "rejected";
      riskTier: "low" | "medium" | "high" | "critical" | null;
      integrityStatus: "PASS" | "NEEDS_ATTENTION" | "UNKNOWN";
      evidenceCompletenessRate: number | null;
      failureSignature: string;
      artifactRefs: string[];
    }>;
  };
  artifactUrl: string;
};

export type LongwallsProductizationSnapshot = {
  controlTower: LongwallsControlTowerMetrics;
  marketplace: {
    packs: LongwallsMarketplacePack[];
    filters: {
      archetypes: string[];
      maturities: string[];
      tags: string[];
    };
  };
  learningLoop: LongwallsLearningLoop;
  releaseRecommendation: LongwallsReleaseRecommendation;
  releaseAttestation: LongwallsReleaseAttestation;
};

export async function fetchLongwallsProductization(projectId: string): Promise<LongwallsProductizationSnapshot> {
  const response = await fetch(`/api/projects/${encodeURIComponent(projectId)}/longwalls/productization`);
  if (!response.ok) {
    throw new Error(`Failed to load Longwalls productization view (${response.status})`);
  }
  const payload = (await response.json()) as {
    ok: boolean;
    productization: LongwallsProductizationSnapshot;
  };
  return payload.productization;
}

